/**
 * Form Filler Service
 *
 * Fills PDF permit application forms using pdf-lib.
 * Supports:
 * - Text fields
 * - Checkboxes
 * - Radio buttons
 * - Field transformations
 * - Form flattening (optional)
 *
 * Part of Phase 4: Form Generation
 */

import { PDFDocument, PDFTextField, PDFCheckBox, PDFRadioGroup, rgb } from 'pdf-lib';
import type {
  FormTemplate,
  FormFieldMapping,
  FormDataSources,
  ResolvedFieldValue,
  FormGenerationResult,
  FormGenerationOptions,
  FieldTransform,
} from '@/types/jurisdiction';

/**
 * Get a value from a nested object using dot notation path
 */
function getValueByPath(obj: unknown, path: string): unknown {
  if (!obj || !path) return undefined;

  const parts = path.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

/**
 * Apply a transformation to a field value
 */
function applyTransform(value: unknown, transform: FieldTransform): string {
  if (value === null || value === undefined) return '';

  const strValue = String(value);

  switch (transform) {
    case 'uppercase':
      return strValue.toUpperCase();

    case 'lowercase':
      return strValue.toLowerCase();

    case 'date_mmddyyyy': {
      const date = new Date(strValue);
      if (isNaN(date.getTime())) return strValue;
      return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
    }

    case 'date_yyyymmdd': {
      const date = new Date(strValue);
      if (isNaN(date.getTime())) return strValue;
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    }

    case 'date_long': {
      const date = new Date(strValue);
      if (isNaN(date.getTime())) return strValue;
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }

    case 'phone_format': {
      const digits = strValue.replace(/\D/g, '');
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
      if (digits.length === 11 && digits[0] === '1') {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
      }
      return strValue;
    }

    case 'currency': {
      const num = parseFloat(strValue);
      if (isNaN(num)) return strValue;
      return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    }

    case 'boolean_yes_no':
      return strValue === 'true' || strValue === '1' || strValue.toLowerCase() === 'yes' ? 'Yes' : 'No';

    case 'boolean_x':
      return strValue === 'true' || strValue === '1' || strValue.toLowerCase() === 'yes' ? 'X' : '';

    case 'truncate_50':
      return strValue.slice(0, 50);

    case 'truncate_100':
      return strValue.slice(0, 100);

    case 'first_name': {
      const parts = strValue.trim().split(/\s+/);
      return parts[0] || '';
    }

    case 'last_name': {
      const parts = strValue.trim().split(/\s+/);
      return parts.length > 1 ? parts.slice(1).join(' ') : '';
    }

    default:
      return strValue;
  }
}

/**
 * Determine the source category from a data path
 */
function getSourceFromPath(dataPath: string): 'job' | 'contractor' | 'jurisdiction' | 'generated' {
  if (dataPath.startsWith('job.')) return 'job';
  if (dataPath.startsWith('contractor.')) return 'contractor';
  if (dataPath.startsWith('jurisdiction.')) return 'jurisdiction';
  if (dataPath.startsWith('generated.')) return 'generated';
  return 'job'; // default
}

/**
 * Resolve a field mapping to a value
 */
function resolveFieldValue(
  mapping: FormFieldMapping,
  dataSources: FormDataSources,
  overrides?: Record<string, string>
): ResolvedFieldValue {
  // Check for user override first
  if (overrides && mapping.formField in overrides) {
    return {
      formField: mapping.formField,
      value: overrides[mapping.formField],
      originalValue: overrides[mapping.formField],
      dataPath: mapping.dataPath,
      usedDefault: false,
      confidence: 'high',
      source: 'user_override',
      isValid: true,
    };
  }

  // Get value from data sources
  const rawValue = getValueByPath(dataSources, mapping.dataPath);
  const source = getSourceFromPath(mapping.dataPath);

  let value: string;
  let usedDefault = false;

  if (rawValue === null || rawValue === undefined || rawValue === '') {
    // Use default value
    value = mapping.defaultValue || '';
    usedDefault = mapping.defaultValue !== undefined;
  } else {
    // Apply transformation if specified
    value = mapping.transform
      ? applyTransform(rawValue, mapping.transform)
      : String(rawValue);
  }

  // Determine confidence
  let confidence: 'high' | 'medium' | 'low' = 'high';
  if (usedDefault && mapping.defaultValue) {
    confidence = 'low';
  } else if (usedDefault) {
    confidence = 'low';
  } else if (source === 'generated') {
    confidence = 'high';
  } else if (!value) {
    confidence = 'low';
  }

  // Validate if required
  const isValid = !mapping.required || (value !== null && value !== undefined && value !== '');
  const validationError = !isValid ? `${mapping.label || mapping.formField} is required` : undefined;

  return {
    formField: mapping.formField,
    value,
    originalValue: rawValue,
    dataPath: mapping.dataPath,
    usedDefault,
    confidence,
    source: usedDefault ? 'default' : source,
    isValid,
    validationError,
  };
}

/**
 * Resolve all field mappings from a template
 */
export function resolveAllFields(
  template: FormTemplate,
  dataSources: FormDataSources,
  overrides?: Record<string, string>
): ResolvedFieldValue[] {
  return template.fieldMappings.map((mapping) =>
    resolveFieldValue(mapping, dataSources, overrides)
  );
}

/**
 * Build data sources from job and contractor data
 */
export function buildDataSources(
  job: Record<string, unknown>,
  contractor: Record<string, unknown>,
  jurisdiction: Record<string, unknown> = {}
): FormDataSources {
  const now = new Date();

  return {
    job: {
      title: job.title as string | undefined,
      address: job.address as string | undefined,
      city: job.city as string | undefined,
      state: job.state as string | undefined,
      zip: job.zip as string | undefined,
      propertyType: job.propertyType as string | undefined,
      permitType: job.permitType as string | undefined,
      jobScope: job.jobScope as string | undefined,
      estimatedValue: job.estimatedValue as number | undefined,
      squareFootage: job.squareFootage as number | undefined,
      description: job.description as string | undefined,
      ownerName: job.ownerName as string | undefined,
      ownerPhone: job.ownerPhone as string | undefined,
      ownerEmail: job.ownerEmail as string | undefined,
      ownerAddress: job.ownerAddress as string | undefined,
      ...job,
    },
    contractor: {
      companyName: contractor.companyName as string | undefined,
      companyAddress: contractor.companyAddress as string | undefined,
      companyCity: contractor.companyCity as string | undefined,
      companyState: contractor.companyState as string | undefined,
      companyZip: contractor.companyZip as string | undefined,
      companyPhone: contractor.companyPhone as string | undefined,
      companyEmail: contractor.companyEmail as string | undefined,
      licenseNumber: contractor.licenseNumber as string | undefined,
      licenseType: contractor.licenseType as string | undefined,
      licenseExpiration: contractor.licenseExpiration as string | undefined,
      qualifierName: contractor.qualifierName as string | undefined,
      insuranceCarrier: contractor.insuranceCarrier as string | undefined,
      insurancePolicyNumber: contractor.insurancePolicyNumber as string | undefined,
      insuranceExpiration: contractor.insuranceExpiration as string | undefined,
      workersCompCarrier: contractor.workersCompCarrier as string | undefined,
      workersCompPolicyNumber: contractor.workersCompPolicyNumber as string | undefined,
      workersCompExpiration: contractor.workersCompExpiration as string | undefined,
      ...contractor,
    },
    jurisdiction: {
      name: jurisdiction.name as string | undefined,
      code: jurisdiction.code as string | undefined,
      isHvhz: jurisdiction.isHvhz as boolean | undefined,
      isFloodZone: jurisdiction.isFloodZone as boolean | undefined,
      ...jurisdiction,
    },
    generated: {
      date: now.toISOString().split('T')[0],
      dateFormatted: now.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      timestamp: now.toISOString(),
    },
  };
}

/**
 * Fetch a PDF template from URL
 */
export async function fetchTemplate(url: string): Promise<ArrayBuffer> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch template: ${response.statusText}`);
  }
  return response.arrayBuffer();
}

/**
 * Fill a PDF form with resolved field values
 */
export async function fillPdfForm(
  templateBytes: ArrayBuffer,
  resolvedFields: ResolvedFieldValue[],
  options: {
    flatten?: boolean;
    highlightMissing?: boolean;
    watermark?: string;
  } = {}
): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.load(templateBytes);
  const form = pdfDoc.getForm();

  // Create a map of field values for quick lookup
  const fieldMap = new Map(resolvedFields.map((f) => [f.formField, f]));

  // Get all form fields
  const fields = form.getFields();

  for (const field of fields) {
    const fieldName = field.getName();
    const resolved = fieldMap.get(fieldName);

    if (!resolved) continue;

    try {
      if (field instanceof PDFTextField) {
        field.setText(resolved.value || '');

        // Highlight missing required fields
        if (options.highlightMissing && !resolved.isValid) {
          // PDF-lib doesn't directly support background color on text fields
          // but we can add a visual indicator in other ways if needed
        }
      } else if (field instanceof PDFCheckBox) {
        if (resolved.value === 'true' || resolved.value === '1' ||
            resolved.value.toLowerCase() === 'yes' || resolved.value === 'X' ||
            resolved.value === 'x') {
          field.check();
        } else {
          field.uncheck();
        }
      } else if (field instanceof PDFRadioGroup) {
        if (resolved.value) {
          try {
            field.select(resolved.value);
          } catch {
            // Radio option doesn't exist, skip
          }
        }
      }
    } catch (error) {
      console.warn(`Failed to fill field ${fieldName}:`, error);
    }
  }

  // Add watermark if specified
  if (options.watermark) {
    const pages = pdfDoc.getPages();
    for (const page of pages) {
      const { width, height } = page.getSize();
      page.drawText(options.watermark, {
        x: width / 2 - 100,
        y: height / 2,
        size: 50,
        color: rgb(0.9, 0.9, 0.9),
        opacity: 0.3,
        rotate: { angle: 45, type: 0 },
      });
    }
  }

  // Flatten the form if requested (makes fields non-editable)
  if (options.flatten) {
    form.flatten();
  }

  return pdfDoc.save();
}

/**
 * Main form generation function
 */
export async function generatePermitForm(
  template: FormTemplate,
  dataSources: FormDataSources,
  options: Omit<FormGenerationOptions, 'jobId' | 'templateId'> = {}
): Promise<FormGenerationResult> {
  const warnings: string[] = [];

  try {
    // Resolve all field values
    const resolvedFields = resolveAllFields(template, dataSources, options.overrides);

    // Calculate completeness
    const totalFields = resolvedFields.length;
    const filledFields = resolvedFields.filter((f) => f.value && f.value.trim() !== '').length;
    const completeness = totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;

    // Find fields needing review (low confidence or missing)
    const fieldsNeedingReview = resolvedFields
      .filter((f) => f.confidence === 'low' || (f.usedDefault && f.value === ''))
      .map((f) => f.formField);

    // Find fields with validation errors
    const fieldsWithErrors = resolvedFields
      .filter((f) => !f.isValid)
      .map((f) => f.formField);

    // Add warnings for low completeness
    if (completeness < 50) {
      warnings.push('Form is less than 50% complete. Consider filling more fields before submission.');
    }

    if (fieldsWithErrors.length > 0) {
      warnings.push(`${fieldsWithErrors.length} required field(s) are missing.`);
    }

    // Fetch and fill the PDF
    const templateBytes = await fetchTemplate(template.templateUrl);
    const pdfBytes = await fillPdfForm(templateBytes, resolvedFields, {
      flatten: false, // Keep editable for now
      highlightMissing: options.highlightMissing,
      watermark: options.includeWatermark ? 'DRAFT - NOT FOR SUBMISSION' : undefined,
    });

    // Create blob
    const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });

    return {
      success: true,
      templateId: template.id,
      fields: resolvedFields,
      fieldsNeedingReview,
      fieldsWithErrors,
      completeness,
      pdfBlob,
      warnings,
    };
  } catch (error) {
    console.error('Form generation failed:', error);
    return {
      success: false,
      templateId: template.id,
      fields: [],
      fieldsNeedingReview: [],
      fieldsWithErrors: [],
      completeness: 0,
      error: error instanceof Error ? error.message : 'Unknown error during form generation',
      warnings,
    };
  }
}

/**
 * Get available fields from a PDF template
 * Useful for creating field mappings
 */
export async function getPdfFormFields(templateUrl: string): Promise<Array<{
  name: string;
  type: 'text' | 'checkbox' | 'radio' | 'dropdown' | 'unknown';
  options?: string[];
}>> {
  const templateBytes = await fetchTemplate(templateUrl);
  const pdfDoc = await PDFDocument.load(templateBytes);
  const form = pdfDoc.getForm();
  const fields = form.getFields();

  return fields.map((field) => {
    const name = field.getName();
    let type: 'text' | 'checkbox' | 'radio' | 'dropdown' | 'unknown' = 'unknown';
    let options: string[] | undefined;

    if (field instanceof PDFTextField) {
      type = 'text';
    } else if (field instanceof PDFCheckBox) {
      type = 'checkbox';
    } else if (field instanceof PDFRadioGroup) {
      type = 'radio';
      options = field.getOptions();
    }

    return { name, type, options };
  });
}

/**
 * Download a generated PDF
 */
export function downloadPdf(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default {
  resolveAllFields,
  buildDataSources,
  fetchTemplate,
  fillPdfForm,
  generatePermitForm,
  getPdfFormFields,
  downloadPdf,
};
